import React, { useContext, memo, useMemo } from "react";
import {
  IonRow,
  IonCol,
  IonCard,
  IonIcon,
  IonButton,
  IonBadge,
  IonText,
  IonCardHeader,
  IonGrid,
  IonCardContent,
} from "@ionic/react";
import { SchedJobContext } from "../context/JobContextScheduler";
import { banOutline, cashOutline, chatboxOutline } from "ionicons/icons";
import { useHistory } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import "../theme/AppStyles.css";
import { WERK_SEGMENT_SELECTOR, WORKFLOW_SCHEDULER_TYPES, FB_COLLECTION_PATH } from "../functions/postActionTypes";
import {
  getStartTs,
  getHoursText,
  createPaymentFilter,
  createStatusFilter,
  createSchedulerSearchFilter,
  createPastJobsFilter,
  sortByDate,
  PastJobFilters,
} from "../utils/jobCardFilters";

const NoOpenJobsList: React.FC = () => {
  return (
    <React.Fragment>
      <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonCol placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <h4>If you had any open jobs they'd be here...</h4>
        </IonCol>
      </IonRow>
    </React.Fragment>
  );
};

const NoFilledJobsList: React.FC = () => {
  return (
    <React.Fragment>
      <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonCol placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <h4>If you had any filled jobs they'd be here...</h4>
        </IonCol>
      </IonRow>
    </React.Fragment>
  );
};

const NoPastJobsList: React.FC = () => {
  return (
    <React.Fragment>
      <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
        <IonCol placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
          <h4>If you had any past jobs they'd be here...</h4>
        </IonCol>
      </IonRow>
    </React.Fragment>
  );
};

type JobCardsSchedulerProps = {
  jobCardSearchText: string;
  segmentSelection: string;
  viewOptions?: { payment: any; status: any; sort: any };
  pastFilters?: PastJobFilters;
  onBeforeNavigate?: () => Promise<void> | void;
  onJobSelect?: (jobDocId: string, uniqueJobId: string, schedUid: string) => void;
  selectedJobId?: string | null;
};

export const JobCardsScheduler: React.FC<JobCardsSchedulerProps> = memo(
  ({ jobCardSearchText, segmentSelection, viewOptions, pastFilters, onBeforeNavigate, onJobSelect, selectedJobId }) => {
    const { schedJob, openJobsCount, filledJobsCount, pastJobsCount, hasMorePast, loadMorePast } = useContext(SchedJobContext);
    const history = useHistory();

    // Clear one badge (isReadBool, jobDocNum)
    const handleClearOneBadgeSched = async (isReadBool: boolean, jobDocNum: string) => {
      if (isReadBool === false) {
        const seenJobId = doc(db, FB_COLLECTION_PATH.JobsDocs, jobDocNum);
        await updateDoc(seenJobId, {
          "jobInfo.status.isRead": true,
        });
      }
      return { cleared: "yes" };
    };

    // Series label (UI only)
    const getSeriesLabel = (job: any): string | null => {
      if (!job?.seriesId) return null;

      if (Number.isFinite(job.seriesIndex) && Number.isFinite(job.seriesTotal) && job.seriesTotal > 0) {
        return `${job.seriesIndex}/${job.seriesTotal}`;
      }

      return "*";
    };

    // UI-only helpers (safe fallbacks) - using imported utilities for getStartTs, getFinishTs, getHoursText

    const getStatusText = (job: any) => {
      const st = job?.JobStatusSched;
      if (st === WORKFLOW_SCHEDULER_TYPES.Open) return "Open";
      if (st === WORKFLOW_SCHEDULER_TYPES.Filled) return "Filled";
      if (st === WORKFLOW_SCHEDULER_TYPES.Past) return "Past";
      return "Open";
    };

    const getCoverageText = (job: any) => {
      // try common patterns safely; if missing, just omit
      const remaining = Number.isFinite(job?.RemainingSlots) ? Number(job?.RemainingSlots) : null;

      const total =
        (Number.isFinite(job?.TotalSlots) ? Number(job?.TotalSlots) : null) ??
        (Number.isFinite(job?.TotalWerkers) ? Number(job?.TotalWerkers) : null) ??
        (Number.isFinite(job?.SlotsNeeded) ? Number(job?.SlotsNeeded) : null) ??
        (Number.isFinite(job?.WerkersNeeded) ? Number(job?.WerkersNeeded) : null) ??
        null;

      if (total != null && remaining != null) {
        const filled = Math.max(0, total - remaining);
        return `${filled}/${total} filled`;
      }

      // fallback if you already store something like AssignedCount
      const assigned =
        (Number.isFinite(job?.AssignedCount) ? Number(job?.AssignedCount) : null) ??
        (Number.isFinite(job?.WerkerCountAssigned) ? Number(job?.WerkerCountAssigned) : null) ??
        null;

      if (assigned != null && total != null) return `${assigned}/${total} filled`;

      return "";
    };

    const getFinishDateText = (job: any) => {
      const raw = job.FinishDateTimeRaw ?? job.finishDateTimeRaw;
      if (!raw) return job.DateDay ?? "";
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return job.DateDay ?? "";
      return new Intl.DateTimeFormat("en-US", {
        month: "numeric",
        day: "numeric",
        year: "2-digit",
      }).format(d);
    };

    const goToDetails = async (job: any) => {
      if (onJobSelect) {
        // Desktop mode: trigger selection handler
        onJobSelect(job.JobDocId, job.JobDocId, job.SchedUid || "");
        await handleClearOneBadgeSched(job.IsRead, job.JobDocId);
      } else {
        // Mobile mode: navigate to details page
        if (onBeforeNavigate) {
          try {
            await onBeforeNavigate();
          } catch (e) {
            // ignore
          }
        }
        await handleClearOneBadgeSched(job.IsRead, job.JobDocId).then(() =>
          history.push(`/SchedShiftDetail/${job.JobDocId}`)
        );
      }
    };

    const displayItems = useMemo(() => {
      const sortMode = viewOptions?.sort ?? "smart";
      const past = segmentSelection === WERK_SEGMENT_SELECTOR.SchedPast;

      // Create filter functions inside memo — deps are data, not unstable function refs
      const matchesPayment = createPaymentFilter(viewOptions?.payment ?? "all", past);
      const matchesStatus = createStatusFilter(viewOptions?.status ?? "all", past);
      const matchesPastFilters = pastFilters ? createPastJobsFilter(pastFilters, past) : null;
      const matchesSearch = createSchedulerSearchFilter(jobCardSearchText);

      // Step 1: filter
      const filtered = (schedJob || [])
        .filter((job: any) => matchesPastFilters ? matchesPastFilters(job) : matchesPayment(job))
        .filter((job: any) => matchesPastFilters ? true : matchesStatus(job))
        .filter((job: any) => {
          if (segmentSelection === WERK_SEGMENT_SELECTOR.SchedOpen) {
            return job.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open;
          } else if (segmentSelection === WERK_SEGMENT_SELECTOR.SchedFilled) {
            return job.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled;
          } else if (segmentSelection === WERK_SEGMENT_SELECTOR.SchedPast) {
            return job.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Past;
          }
          return false;
        })
        .filter((job: any) => matchesSearch(job));

      // Step 2: If explicit sort (not smart), flat chronological — no grouping
      if (sortMode === "date_newest" || sortMode === "date_oldest") {
        const sorted = sortByDate(filtered, sortMode);
        return sorted.map((job: any) => ({ job, seriesDivider: null as string | null }));
      }

      // Step 3: Smart sort — group by series
      const seriesGroups = new Map<string, any[]>(); // seriesId -> jobs
      const standalone: any[] = [];

      for (const job of filtered) {
        if (job.seriesId) {
          const group = seriesGroups.get(job.seriesId);
          if (group) {
            group.push(job);
          } else {
            seriesGroups.set(job.seriesId, [job]);
          }
        } else {
          standalone.push(job);
        }
      }

      // Sort jobs within each series group by seriesIndex (or start time fallback)
      Array.from(seriesGroups.values()).forEach((group) => {
        group.sort((a: any, b: any) => {
          const ai = Number.isFinite(a.seriesIndex) ? a.seriesIndex : 999;
          const bi = Number.isFinite(b.seriesIndex) ? b.seriesIndex : 999;
          if (ai !== bi) return ai - bi;
          return getStartTs(a) - getStartTs(b);
        });
      });

      // Build sortable entries: each group gets its earliest start time as sort key
      type GroupEntry = { type: "series"; seriesId: string; jobs: any[]; sortTs: number; hasUnread: boolean }
        | { type: "standalone"; job: any; sortTs: number; hasUnread: boolean };

      const entries: GroupEntry[] = [];

      Array.from(seriesGroups.entries()).forEach(([seriesId, jobs]) => {
        const sortTs = Math.min(...jobs.map(getStartTs));
        const hasUnread = jobs.some((j: any) => j.IsRead === false);
        entries.push({ type: "series", seriesId, jobs, sortTs, hasUnread });
      });

      for (const job of standalone) {
        entries.push({ type: "standalone", job, sortTs: getStartTs(job), hasUnread: job.IsRead === false });
      }

      // Sort entries: unread first, then by start time (past: newest first, else soonest first)
      entries.sort((a, b) => {
        if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
        if (a.sortTs !== b.sortTs) return past ? b.sortTs - a.sortTs : a.sortTs - b.sortTs;
        return 0;
      });

      // Flatten into display items with optional divider
      const items: { job: any; seriesDivider: string | null }[] = [];
      for (const entry of entries) {
        if (entry.type === "series") {
          const total = entry.jobs[0]?.seriesTotal ?? entry.jobs.length;
          items.push({ job: entry.jobs[0], seriesDivider: `Series of ${total}` });
          for (let i = 1; i < entry.jobs.length; i++) {
            items.push({ job: entry.jobs[i], seriesDivider: null });
          }
        } else {
          items.push({ job: entry.job, seriesDivider: null });
        }
      }

      return items;
    }, [schedJob, segmentSelection, viewOptions, pastFilters, jobCardSearchText]);

    return (
      <React.Fragment>
        {segmentSelection === WERK_SEGMENT_SELECTOR.SchedOpen && openJobsCount === 0 && <NoOpenJobsList />}
        {segmentSelection === WERK_SEGMENT_SELECTOR.SchedFilled && filledJobsCount === 0 && <NoFilledJobsList />}
        {segmentSelection === WERK_SEGMENT_SELECTOR.SchedPast && pastJobsCount === 0 && <NoPastJobsList />}

        {displayItems.map(({ job, seriesDivider }) => {
            const seriesLabel = getSeriesLabel(job);

            const statusText = getStatusText(job);
            const coverageText = getCoverageText(job);

            const hoursText = getHoursText(job);
            const finishDateText = getFinishDateText(job);

            // Keep your existing “display strings” (facelift-only)
            const timeLineLeft = `${job.DateDay ?? ""} • ${job.StartDateTime ?? ""}`;
            const endTimeLineLeft = `${finishDateText} • ${job.FinishDateTime ?? ""}${
              hoursText ? ` • ${hoursText}` : ""
            }`;
            
            return (              <React.Fragment key={job.JobDocId}>
                {seriesDivider && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      margin: "16px 16px 4px 16px",
                      gap: "10px",
                    }}
                  >
                    <div style={{ flex: 1, height: "1px", backgroundColor: "var(--ion-color-medium)", opacity: 0.35 }} />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "var(--ion-color-medium)",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {seriesDivider}
                    </span>
                    <div style={{ flex: 1, height: "1px", backgroundColor: "var(--ion-color-medium)", opacity: 0.35 }} />
                  </div>
                )}
              <IonCard
                style={{
                  margin: "12px 12px",
                  borderRadius: "7px",
                  overflow: "hidden",
                  position: "relative",
                  height: "149px",
                  display: "flex",
                  flexDirection: "column",
                  border: selectedJobId === job.JobDocId ? "3px solid var(--ion-color-primary)" : undefined,
                  backgroundColor: selectedJobId === job.JobDocId ? "var(--ion-color-light)" : undefined,
                }}
                className={job.IsRead === false ? "cardOutlineScheduler" : ""}
                onClick={() => void goToDetails(job)} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}              >
                <IonCardHeader
                  style={{ padding: "10px 12px 6px" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                >
                  <IonGrid
                    fixed={true}
                    style={{ padding: 0 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                  >
                    <IonRow
                      class="ion-align-items-center" placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                    >
                      {/* Title */}
                      <IonCol
                        style={{ padding: 0, minWidth: 0 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonText
                          style={{
                            display: "block",
                            fontWeight: 800,
                            fontSize: "15px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          {job.ShiftIdentifier ?? ""}
                        </IonText>
                      </IonCol>

                      {/* Right cluster: statuses + chat (uniform) */}
                      <IonCol
                        size="auto"
                        style={{
                          padding: 0,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          position: "relative",
                        }}
                        onClick={(e) => {
                          // prevent accidental card click when tapping icons
                          e.stopPropagation();
                        } } placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        {job.JobCancelledByScheduler === true && (
                          <IonIcon
                            icon={banOutline}
                            color="danger"
                            style={{ fontSize: "18px", opacity: 0.95 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                          />
                        )}

                        {job.PaidWerkers === true && (
                          <IonIcon
                            icon={cashOutline}
                            color="success"
                            style={{ fontSize: "18px", opacity: 0.95 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                          />
                        )}

                        {seriesLabel && (
                          <IonBadge
                            color="medium"
                            style={{ fontSize: "11px", padding: "3px 8px", borderRadius: "999px" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                          >
                            {seriesLabel}
                          </IonBadge>
                        )}

                        {/* Chat icon always shown; unread indicated by badge (no "New" text) */}
                        <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                          <IonIcon
                            icon={chatboxOutline}
                            style={{ fontSize: "20px", paddingRight: "4px", opacity: 0.7 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                          />

                          {job.IsNewMessage === true && (
                            <IonBadge
                              color="danger"
                              style={{
                                position: "absolute",
                                top: "-3px",
                                right: "1px",
                                fontSize: "1px",
                                padding: "4px 4px",
                                // borderRadius: "999px",
                              }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                            >
                              {/* dot badge */}{" "}
                            </IonBadge>
                          )}
                        </div>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardHeader>

                <IonCardContent
                  style={{
                    padding: "0 12px 6px", // ✅ slightly less bottom padding
                    flex: "1 1 auto", // ✅ fill remaining height
                    minHeight: 0, // ✅ allows children to shrink instead of overflow
                    display: "flex",
                    flexDirection: "column",
                  }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                >
                  <IonGrid
                    fixed={true}
                    style={{ padding: 0 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                  >
                    {/* Timeline + Pay */}
                    <IonRow
                      style={{ paddingTop: "5px" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                    >
                      <IonCol
                        style={{ padding: 0, minWidth: 0, flex: "1 1 55%" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonText
                          style={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 400,
                            opacity: 0.95,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          <span style={{ fontSize: "10px", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em", marginRight: 4 }}>Start</span>
                          {timeLineLeft}
                        </IonText>
                        <IonText
                          style={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 400,
                            opacity: 0.95,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          <span style={{ fontSize: "10px", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em", marginRight: 4 }}>End</span>
                          {endTimeLineLeft}
                        </IonText>
                      </IonCol>

                      <IonCol
                        style={{
                          padding: 0,
                          paddingLeft: "10px",
                          maxWidth: "40%",
                          flex: "0 1 40%",
                          minWidth: 0,
                          textAlign: "right",
                        }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonText
                          style={{
                            display: "block",
                            fontWeight: 900,
                            fontSize: "13px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          {job.Pay ?? ""}
                        </IonText>
                      </IonCol>
                    </IonRow>

                    {/* Company */}
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonCol
                        style={{ padding: 0, marginTop: "2px" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonText
                          style={{
                            display: "block",
                            fontWeight: 900,
                            fontSize: "14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          {job.Company ?? ""}
                        </IonText>
                      </IonCol>
                    </IonRow>

                    {/* Location */}
                    <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                      <IonCol
                        style={{ padding: 0 }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonText
                          style={{
                            display: "block",
                            fontSize: "13px",
                            fontWeight: 500,
                            opacity: 0.92,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          {job.Location ?? ""}
                        </IonText>
                      </IonCol>
                    </IonRow>

                    {/* Bottom row: Status/Coverage (left) + Details (right) */}
                    <IonRow
                      style={{ alignItems: "center", paddingTop: "2px" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                    >
                      <IonCol
                        style={{
                          padding: 0,
                          maxWidth: "75%",
                          // flex: "0 1 75%",
                          minWidth: 0,
                        }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonText
                          style={{
                            display: "block",
                            fontSize: "12px",
                            fontWeight: 500,
                            opacity: 0.78,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          {coverageText ? `${statusText} • ${coverageText}` : statusText}
                        </IonText>
                      </IonCol>

                      <IonCol
                        size="auto"
                        style={{
                          padding: 0,
                          marginLeft: "auto",
                          display: "flex",
                          justifyContent: "flex-end",
                        }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                      >
                        <IonButton
                          fill="outline"
                          size="small"
                          color="medium"
                          style={{ margin: 0, height: "24px" }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            await goToDetails(job);
                          } } placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}                        >
                          Details
                        </IonButton>
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>
              </React.Fragment>
            );
          })}

        {segmentSelection === WERK_SEGMENT_SELECTOR.SchedPast && hasMorePast && (
          <IonRow placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
            <IonCol style={{ textAlign: "center", padding: "12px" }} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
              <IonButton fill="outline" size="small" onClick={() => loadMorePast()} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                Load more
              </IonButton>
            </IonCol>
          </IonRow>
        )}
      </React.Fragment>
    );
  }
);
